import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Star, Check, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import GdprConsent from "@/components/GdprConsent";

export default function SubmitReview() {
  const [params] = useSearchParams();
  const bid = params.get("b") || "";
  const presetRating = Math.max(0, Math.min(5, parseInt(params.get("r") || "0", 10)));
  const [rating, setRating] = useState(presetRating || 0);
  const [hover, setHover] = useState(0);
  const [form, setForm] = useState({ name: "", town: "", text: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [gdpr, setGdpr] = useState(false);

  useEffect(() => {
    document.title = "Napíšte hodnotenie — Zateplenia.eu";
    window.scrollTo(0, 0);
    if (bid) {
      api.get(`/bookings/${bid}`).then((r) => {
        setForm((f) => ({ ...f, name: r.data?.name || "", town: r.data?.address || "" }));
      }).catch(() => {});
    }
  }, [bid]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!rating) { toast.error("Vyberte počet hviezdičiek."); return; }
    if (!form.name.trim() || !form.text.trim()) { toast.error("Vyplňte meno a text hodnotenia."); return; }
    if (!gdpr) { toast.error("Potvrďte súhlas so spracovaním osobných údajov."); return; }
    setSending(true);
    try {
      await api.post("/reviews", { name: form.name, town: form.town, rating, text: form.text });
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Odoslanie zlyhalo. Skúste to znova.");
    } finally { setSending(false); }
  };

  return (
    <div className="bg-ink min-h-screen">
      <Header />
      <section className="max-w-[720px] mx-auto px-5 md:px-10 pt-32 pb-24" data-testid="submit-review-page">
        <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-gold text-sm mb-8 font-mono2 uppercase tracking-widest">
          <ArrowLeft className="w-4 h-4" /> Späť na hlavnú
        </Link>

        {done ? (
          <div className="border border-gold/30 bg-ink-2 p-10 text-center" data-testid="review-thanks">
            <div className="w-16 h-16 rounded-full bg-gold/15 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-gold" />
            </div>
            <h1 className="font-display text-4xl text-white">Ďakujeme za hodnotenie!</h1>
            <p className="text-white/60 mt-4 leading-relaxed">Vašu recenziu sme prijali. Po krátkom overení ju zverejníme na našej stránke. Vážime si váš čas.</p>
            <Link to="/" className="inline-block mt-8 bg-gold text-ink px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-gold-light transition-colors">
              Späť na hlavnú stránku
            </Link>
          </div>
        ) : (
          <>
            <p className="font-mono2 text-gold uppercase tracking-[0.3em] text-xs mb-5">Vaše hodnotenie</p>
            <h1 className="font-display text-4xl sm:text-5xl leading-[0.98] text-white">Ako ste boli spokojní?</h1>
            <p className="text-white/55 text-sm md:text-base mt-4">Vaša spätná väzba nám pomáha zlepšovať sa a ostatným klientom pomáha pri rozhodovaní.</p>

            {/* stars */}
            <div className="flex items-center gap-2 mt-10" data-testid="review-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => setRating(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                  data-testid={`star-${n}`} aria-label={`${n} hviezdičiek`} className="transition-transform hover:scale-110">
                  <Star className={`w-10 h-10 ${n <= (hover || rating) ? "text-gold fill-gold" : "text-white/25"}`} />
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
              <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Vaše meno" data-testid="review-input-name"
                className="bg-ink-2 border border-white/15 focus:border-gold outline-none text-white px-4 py-3 text-sm" />
              <input value={form.town} onChange={(e) => set("town", e.target.value)} placeholder="Mesto / okres (voliteľné)" data-testid="review-input-town"
                className="bg-ink-2 border border-white/15 focus:border-gold outline-none text-white px-4 py-3 text-sm" />
            </div>
            <textarea value={form.text} onChange={(e) => set("text", e.target.value)} rows={5} placeholder="Napíšte, ako ste boli spokojní s realizáciou, komunikáciou a výsledkom…" data-testid="review-input-text"
              className="w-full mt-4 bg-ink-2 border border-white/15 focus:border-gold outline-none text-white px-4 py-3 text-sm" />

            <GdprConsent checked={gdpr} onChange={setGdpr} testid="review-gdpr" className="mt-5" />

            <button onClick={submit} disabled={sending} data-testid="review-submit-btn"
              className="mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gold text-ink px-8 py-4 font-bold uppercase tracking-wider text-sm hover:bg-gold-light transition-colors disabled:opacity-50">
              {sending ? "Odosielam…" : "Odoslať hodnotenie"}
            </button>
            <p className="text-white/30 text-xs mt-4">Recenzia bude zverejnená po krátkom overení.</p>
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}
